import { PythonTopicExercise } from '../../../../types/python-topic';

export const algoritmosListasExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Comprensión de listas básica',
    description: 'Aprende a usar comprensión de listas para crear listas de forma concisa y eficiente.',
    initialCode: `# Crea una lista de números pares del 0 al 10
# Usa comprensión de listas
pares = 

# Crea una lista de cuadrados de números del 1 al 5
cuadrados = 

print(f"Números pares: {pares}")
print(f"Cuadrados: {cuadrados}")`,
    solution: `# Crea una lista de números pares del 0 al 10
# Usa comprensión de listas
pares = [x for x in range(11) if x % 2 == 0]

# Crea una lista de cuadrados de números del 1 al 5
cuadrados = [x**2 for x in range(1, 6)]

print(f"Números pares: {pares}")
print(f"Cuadrados: {cuadrados}")`,
    testCases: [],
    hints: [
      'La sintaxis es: [expresion for elemento in iterable]',
      'Puedes agregar condiciones con if',
      'Puedes transformar elementos con expresiones',
      'Es más conciso que bucles for tradicionales'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar comprensión de listas?',
      content: `Las **comprensiones de listas** son una forma concisa y eficiente de crear listas. Reducen código y mejoran legibilidad.

### ¿Por qué este ejercicio?
- **Concisión**: Menos código que bucles for tradicionales
- **Legibilidad**: Más fácil de leer cuando te acostumbras
- **Eficiencia**: Generalmente más rápidas que bucles
- **Pythonic**: Estilo de código característico de Python

### En el mundo real:
Cuando un sistema procesa una lista de productos y necesita crear una lista de precios con descuento, usa comprensión de listas: [precio * 0.9 for precio in precios]. Sin comprensión de listas, necesitarías un bucle for más largo.`,
      keyPoints: [
        'Sintaxis: [expresion for elemento in iterable]',
        'Puedes agregar condiciones con if',
        'Más conciso que bucles for tradicionales',
        'Estilo Pythonic característico'
      ],
      realWorldExample: 'En un sistema de usuarios, creas una lista de emails de usuarios activos: [user.email for user in usuarios if user.activo].'
    },
    theory: `# Comprensión de Listas

## ¿Qué es una Comprensión de Lista?

Una **comprensión de lista** es una forma concisa de crear listas basadas en iterables existentes.

## Sintaxis Básica

\`\`\`python
# Forma tradicional
cuadrados = []
for x in range(1, 6):
    cuadrados.append(x**2)

# Con comprensión de listas
cuadrados = [x**2 for x in range(1, 6)]
\`\`\`

## Con Condiciones

Puedes agregar condiciones con \`if\`:

\`\`\`python
# Solo números pares
pares = [x for x in range(11) if x % 2 == 0]
\`\`\`

## Ventajas

- **Concisión**: Menos código
- **Legibilidad**: Más claro cuando te acostumbras
- **Eficiencia**: Generalmente más rápido
- **Pythonic**: Estilo característico de Python`,
    tips: [
      'Sintaxis: [expresion for elemento in iterable]',
      'Puedes agregar condiciones con if',
      'Puedes transformar elementos con expresiones',
      'Es más conciso que bucles for tradicionales',
      'Practica con ejemplos simples primero'
    ],
    advice: [
      'Usa comprensión de listas cuando sea más legible',
      'No abuses - a veces un bucle for es más claro',
      'Practica transformando bucles for a comprensiones',
      'Aprende a leer comprensiones de listas complejas'
    ]
  },
  {
    id: 2,
    title: 'Filtrar y transformar con comprensión',
    description: 'Aprende a combinar filtrado y transformación en comprensiones de listas.',
    initialCode: `# Lista de números
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Crea una lista de cuadrados de números pares
cuadrados_pares = 

print(f"Cuadrados de números pares: {cuadrados_pares}")`,
    solution: `# Lista de números
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Crea una lista de cuadrados de números pares
cuadrados_pares = [x**2 for x in numeros if x % 2 == 0]

print(f"Cuadrados de números pares: {cuadrados_pares}")`,
    testCases: [],
    hints: [
      'Combina transformación (x**2) con filtro (if x % 2 == 0)',
      'La sintaxis es: [expresion for elemento in iterable if condicion]',
      'Primero filtra, luego transforma',
      'Puedes combinar múltiples condiciones'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué combinar filtrado y transformación?',
      content: `**Combinar filtrado y transformación** en una sola operación es muy común y eficiente. Te permite procesar datos en un solo paso.

### ¿Por qué este ejercicio?
- **Eficiencia**: Una sola operación en lugar de dos
- **Legibilidad**: Código más claro y expresivo
- **Aplicación práctica**: Muy común en procesamiento de datos
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando un sistema procesa productos y necesita precios con descuento solo de productos en oferta, usa: [precio * 0.9 for precio in precios if producto.en_oferta]. Sin comprensiones, necesitarías múltiples pasos.`,
      keyPoints: [
        'Puedes combinar transformación y filtrado',
        'Sintaxis: [expresion for elemento in iterable if condicion]',
        'Más eficiente que hacerlo en pasos separados',
        'Muy común en procesamiento de datos'
      ],
      realWorldExample: 'En un sistema de calificaciones, obtienes notas aprobadas con descuento: [nota * 1.1 for nota in notas if nota >= 6].'
    },
    theory: `# Filtrar y Transformar con Comprensión

## Combinar Operaciones

Puedes combinar filtrado y transformación:

\`\`\`python
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
cuadrados_pares = [x**2 for x in numeros if x % 2 == 0]
\`\`\`

## Orden de Operaciones

1. **Filtra**: \`if x % 2 == 0\` selecciona solo pares
2. **Transforma**: \`x**2\` calcula el cuadrado

## Múltiples Condiciones

Puedes usar múltiples condiciones:

\`\`\`python
resultado = [x**2 for x in numeros if x % 2 == 0 if x > 5]
\`\`\`

## Ventajas

- **Eficiencia**: Una sola operación
- **Legibilidad**: Código más claro
- **Expresividad**: Describe exactamente lo que quieres`,
    tips: [
      'Combina transformación y filtrado en una comprensión',
      'La sintaxis es: [expresion for elemento in iterable if condicion]',
      'Puedes usar múltiples condiciones',
      'Practica con diferentes combinaciones'
    ],
    advice: [
      'Combina operaciones cuando sea más claro',
      'No abuses - a veces pasos separados son más legibles',
      'Practica leyendo comprensiones complejas',
      'Estas técnicas son fundamentales en Python'
    ]
  },
  {
    id: 3,
    title: 'Comprensión de listas anidadas',
    description: 'Aprende a usar comprensión de listas anidadas para crear estructuras bidimensionales.',
    initialCode: `# Crea una matriz 3x3 usando comprensión de listas anidadas
# Cada fila debe tener números del 0 al 2
matriz = 

print(f"Matriz: {matriz}")`,
    solution: `# Crea una matriz 3x3 usando comprensión de listas anidadas
# Cada fila debe tener números del 0 al 2
matriz = [[j for j in range(3)] for i in range(3)]

print(f"Matriz: {matriz}")`,
    testCases: [],
    hints: [
      'Usa comprensión de listas anidadas: [[...] for ...]',
      'La comprensión externa crea las filas',
      'La comprensión interna crea los elementos de cada fila',
      'Puedes usar diferentes variables para cada nivel'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué comprensión de listas anidadas?',
      content: `Las **comprensiones de listas anidadas** permiten crear estructuras bidimensionales (matrices) de forma concisa y eficiente.

### ¿Por qué este ejercicio?
- **Estructuras complejas**: Crear matrices y estructuras bidimensionales
- **Concisión**: Más corto que bucles anidados tradicionales
- **Aplicación práctica**: Matrices, tablas, estructuras de datos
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando un sistema crea una tabla de multiplicación, usa comprensión de listas anidadas: [[i*j for j in range(1, 11)] for i in range(1, 11)]. Sin comprensiones anidadas, necesitarías bucles for anidados.`,
      keyPoints: [
        'Comprensiones anidadas crean estructuras bidimensionales',
        'Sintaxis: [[expresion for j in ...] for i in ...]',
        'Más conciso que bucles anidados',
        'Útil para matrices y tablas'
      ],
      realWorldExample: 'En un sistema de gráficos, creas una matriz de píxeles: [[0 for j in range(ancho)] for i in range(alto)].'
    },
    theory: `# Comprensión de Listas Anidadas

## ¿Qué son Comprensiones Anidadas?

Las **comprensiones de listas anidadas** permiten crear listas de listas (matrices) de forma concisa.

## Sintaxis

\`\`\`python
# Matriz 3x3
matriz = [[j for j in range(3)] for i in range(3)]
# [[0, 1, 2], [0, 1, 2], [0, 1, 2]]
\`\`\`

## Cómo Funciona

1. **Comprensión externa**: Crea las filas
2. **Comprensión interna**: Crea los elementos de cada fila

## Ejemplo: Tabla de Multiplicación

\`\`\`python
tabla = [[i*j for j in range(1, 11)] for i in range(1, 11)]
\`\`\`

## Ventajas

- **Concisión**: Menos código que bucles anidados
- **Legibilidad**: Más claro cuando te acostumbras
- **Eficiencia**: Generalmente más rápido`,
    tips: [
      'Usa comprensión de listas anidadas para matrices',
      'Sintaxis: [[expresion for j in ...] for i in ...]',
      'La comprensión externa crea las filas',
      'La comprensión interna crea los elementos',
      'Puedes usar diferentes variables para cada nivel'
    ],
    advice: [
      'Practica con matrices simples primero',
      'Experimenta con diferentes tamaños',
      'Aprende a leer comprensiones anidadas',
      'Practica creando estructuras bidimensionales'
    ]
  },
  {
    id: 4,
    title: 'Iterar sobre listas con enumerate',
    description: 'Aprende a usar enumerate() para obtener índices y valores al iterar sobre listas.',
    initialCode: `# Lista de nombres
nombres = ["Ana", "Juan", "María", "Pedro"]

# Itera sobre la lista y muestra índice y nombre
# Usa enumerate() para obtener (índice, valor)
for indice, nombre in :
    print(f"Índice {indice}: {nombre}")`,
    solution: `# Lista de nombres
nombres = ["Ana", "Juan", "María", "Pedro"]

# Itera sobre la lista y muestra índice y nombre
# Usa enumerate() para obtener (índice, valor)
for indice, nombre in enumerate(nombres):
    print(f"Índice {indice}: {nombre}")`,
    testCases: [],
    hints: [
      'enumerate(lista) devuelve (índice, valor)',
      'Puedes desempaquetar en el for: for i, valor in enumerate(lista)',
      'Útil cuando necesitas el índice y el valor',
      'Más legible que usar range(len(lista))'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar enumerate()?',
      content: `**enumerate()** permite obtener el índice y el valor al iterar sobre una lista, haciendo el código más legible y Pythonic.

### ¿Por qué este ejercicio?
- **Legibilidad**: Más claro que usar range(len(lista))',
      '- **Conveniencia**: Obtienes índice y valor en una sola operación
- **Aplicación práctica**: Muy común cuando necesitas índices
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando un sistema procesa una lista de productos y necesita mostrar su posición en el ranking, usa enumerate(): for pos, producto in enumerate(productos). Sin enumerate(), tendrías que usar range(len(productos)).`,
      keyPoints: [
        'enumerate() devuelve (índice, valor)',
        'Puedes desempaquetar en el for',
        'Más legible que range(len(lista))',
        'Útil cuando necesitas índices'
      ],
      realWorldExample: 'En un sistema de logs, enumeras mensajes: for i, mensaje in enumerate(logs) para mostrar el número de mensaje.'
    },
    theory: `# Iterar con enumerate()

## ¿Qué es enumerate()?

**enumerate()** devuelve pares (índice, valor) al iterar sobre una lista.

## Sintaxis

\`\`\`python
nombres = ["Ana", "Juan", "María"]
for indice, nombre in enumerate(nombres):
    print(f"{indice}: {nombre}")
\`\`\`

## Comparación

### Sin enumerate()
\`\`\`python
for i in range(len(nombres)):
    print(f"{i}: {nombres[i]}")
\`\`\`

### Con enumerate()
\`\`\`python
for i, nombre in enumerate(nombres):
    print(f"{i}: {nombre}")
\`\`\`

## Ventajas

- **Legibilidad**: Código más claro
- **Conveniencia**: Índice y valor juntos
- **Pythonic**: Estilo característico`,
    tips: [
      'enumerate() devuelve (índice, valor)',
      'Puedes desempaquetar en el for',
      'Más legible que range(len(lista))',
      'Útil cuando necesitas índices',
      'Puedes especificar el índice inicial: enumerate(lista, start=1)'
    ],
    advice: [
      'Usa enumerate() cuando necesites índices',
      'Practica desempaquetando tuplas',
      'Aprende cuándo usar enumerate() vs range()',
      'Practica en contextos donde necesites índices'
    ]
  },
  {
    id: 5,
    title: 'Iterar sobre múltiples listas con zip',
    description: 'Aprende a usar zip() para iterar sobre múltiples listas simultáneamente.',
    initialCode: `# Dos listas relacionadas
nombres = ["Ana", "Juan", "María"]
edades = [25, 30, 28]

# Itera sobre ambas listas simultáneamente usando zip()
for nombre, edad in :
    print(f"{nombre} tiene {edad} años")`,
    solution: `# Dos listas relacionadas
nombres = ["Ana", "Juan", "María"]
edades = [25, 30, 28]

# Itera sobre ambas listas simultáneamente usando zip()
for nombre, edad in zip(nombres, edades):
    print(f"{nombre} tiene {edad} años")`,
    testCases: [],
    hints: [
      'zip(lista1, lista2) combina elementos de ambas listas',
      'Puedes desempaquetar en el for: for a, b in zip(lista1, lista2)',
      'Útil cuando tienes datos relacionados en listas separadas',
      'Se detiene cuando la lista más corta termina'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar zip()?',
      content: `**zip()** permite iterar sobre múltiples listas simultáneamente, combinando elementos relacionados de forma elegante.

### ¿Por qué este ejercicio?
- **Sincronización**: Itera sobre listas relacionadas juntas
- **Legibilidad**: Código más claro que usar índices
- **Aplicación práctica**: Muy común cuando tienes datos relacionados
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando un sistema procesa productos con precios en listas separadas, usa zip(): for producto, precio in zip(productos, precios). Sin zip(), tendrías que usar índices manualmente.`,
      keyPoints: [
        'zip() combina elementos de múltiples listas',
        'Puedes desempaquetar en el for',
        'Se detiene cuando la lista más corta termina',
        'Útil para datos relacionados'
      ],
      realWorldExample: 'En un sistema de usuarios, combinas nombres y emails: for nombre, email in zip(nombres, emails).'
    },
    theory: `# Iterar con zip()

## ¿Qué es zip()?

**zip()** combina elementos de múltiples listas, permitiendo iterar sobre ellas simultáneamente.

## Sintaxis

\`\`\`python
nombres = ["Ana", "Juan"]
edades = [25, 30]
for nombre, edad in zip(nombres, edades):
    print(f"{nombre}: {edad}")
\`\`\`

## Características

### Se Detiene con la Lista Más Corta
\`\`\`python
lista1 = [1, 2, 3]
lista2 = ["a", "b"]
for a, b in zip(lista1, lista2):
    print(a, b)  # Solo itera 2 veces
\`\`\`

## Múltiples Listas

Puedes combinar más de dos listas:
\`\`\`python
for a, b, c in zip(lista1, lista2, lista3):
    pass
\`\`\`

## Ventajas

- **Sincronización**: Itera sobre listas relacionadas
- **Legibilidad**: Código más claro
- **Conveniencia**: No necesitas índices`,
    tips: [
      'zip() combina elementos de múltiples listas',
      'Puedes desempaquetar en el for',
      'Se detiene cuando la lista más corta termina',
      'Útil para datos relacionados',
      'Puedes combinar más de dos listas'
    ],
    advice: [
      'Usa zip() cuando tengas listas relacionadas',
      'Practica desempaquetando múltiples valores',
      'Aprende cuándo usar zip() vs índices',
      'Practica en contextos con datos relacionados'
    ]
  },
  {
    id: 6,
    title: 'Comprensión de diccionarios',
    description: 'Aprende a usar comprensión de diccionarios para crear diccionarios de forma concisa.',
    initialCode: `# Lista de números
numeros = [1, 2, 3, 4, 5]

# Crea un diccionario donde la clave es el número y el valor es su cuadrado
# Usa comprensión de diccionarios: {clave: valor for elemento in iterable}
cuadrados = 

print(f"Cuadrados: {cuadrados}")`,
    solution: `# Lista de números
numeros = [1, 2, 3, 4, 5]

# Crea un diccionario donde la clave es el número y el valor es su cuadrado
# Usa comprensión de diccionarios: {clave: valor for elemento in iterable}
cuadrados = {n: n**2 for n in numeros}

print(f"Cuadrados: {cuadrados}")`,
    testCases: [],
    hints: [
      'Sintaxis: {clave: valor for elemento in iterable}',
      'Similar a comprensión de listas pero con llaves {}',
      'Puedes agregar condiciones con if',
      'Útil para crear diccionarios de forma concisa'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar comprensión de diccionarios?',
      content: `Las **comprensiones de diccionarios** permiten crear diccionarios de forma concisa y eficiente, similar a las comprensiones de listas.

### ¿Por qué este ejercicio?
- **Concisión**: Menos código que bucles for tradicionales
- **Legibilidad**: Más claro cuando te acostumbras
- **Aplicación práctica**: Crear diccionarios de mapeo, índices
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando un sistema crea un índice de productos por ID, usa comprensión de diccionarios: {producto.id: producto for producto in productos}. Sin comprensiones, necesitarías un bucle for más largo.`,
      keyPoints: [
        'Sintaxis: {clave: valor for elemento in iterable}',
        'Similar a comprensión de listas pero con llaves',
        'Puedes agregar condiciones con if',
        'Útil para crear diccionarios de forma concisa'
      ],
      realWorldExample: 'En un sistema de usuarios, creas un índice por email: {usuario.email: usuario for usuario in usuarios}.'
    },
    theory: `# Comprensión de Diccionarios

## ¿Qué es Comprensión de Diccionarios?

Las **comprensiones de diccionarios** permiten crear diccionarios de forma concisa.

## Sintaxis

\`\`\`python
numeros = [1, 2, 3, 4, 5]
cuadrados = {n: n**2 for n in numeros}
# {1: 1, 2: 4, 3: 9, 4: 16, 5: 25}
\`\`\`

## Con Condiciones

Puedes agregar condiciones:
\`\`\`python
pares_cuadrados = {n: n**2 for n in numeros if n % 2 == 0}
\`\`\`

## Comparación

### Sin Comprensión
\`\`\`python
cuadrados = {}
for n in numeros:
    cuadrados[n] = n**2
\`\`\`

### Con Comprensión
\`\`\`python
cuadrados = {n: n**2 for n in numeros}
\`\`\`

## Ventajas

- **Concisión**: Menos código
- **Legibilidad**: Más claro
- **Eficiencia**: Generalmente más rápido`,
    tips: [
      'Sintaxis: {clave: valor for elemento in iterable}',
      'Similar a comprensión de listas pero con llaves',
      'Puedes agregar condiciones con if',
      'Útil para crear diccionarios de forma concisa',
      'Practica con ejemplos simples primero'
    ],
    advice: [
      'Usa comprensión de diccionarios cuando sea más legible',
      'Practica transformando bucles for a comprensiones',
      'Aprende a leer comprensiones de diccionarios',
      'Practica creando diferentes tipos de mapeos'
    ]
  },
  {
    id: 7,
    title: 'Comprensión de sets',
    description: 'Aprende a usar comprensión de sets para crear sets de forma concisa.',
    initialCode: `# Lista de números con duplicados
numeros = [1, 2, 2, 3, 3, 3, 4, 4, 4, 4]

# Crea un set con los cuadrados de los números únicos
# Usa comprensión de sets: {expresion for elemento in iterable}
cuadrados_unicos = 

print(f"Cuadrados únicos: {cuadrados_unicos}")`,
    solution: `# Lista de números con duplicados
numeros = [1, 2, 2, 3, 3, 3, 4, 4, 4, 4]

# Crea un set con los cuadrados de los números únicos
# Usa comprensión de sets: {expresion for elemento in iterable}
cuadrados_unicos = {n**2 for n in numeros}

print(f"Cuadrados únicos: {cuadrados_unicos}")`,
    testCases: [],
    hints: [
      'Sintaxis: {expresion for elemento in iterable}',
      'Similar a comprensión de listas pero crea un set',
      'Los sets eliminan duplicados automáticamente',
      'Útil para crear sets de forma concisa'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar comprensión de sets?',
      content: `Las **comprensiones de sets** permiten crear sets de forma concisa, eliminando duplicados automáticamente.

### ¿Por qué este ejercicio?
- **Eliminación de duplicados**: Automática en sets
- **Concisión**: Menos código que bucles for tradicionales
- **Aplicación práctica**: Valores únicos, eliminación de duplicados
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando un sistema procesa tags de productos y necesita valores únicos, usa comprensión de sets: {tag.lower() for tag in tags}. Sin comprensiones, necesitarías convertir a set manualmente.`,
      keyPoints: [
        'Sintaxis: {expresion for elemento in iterable}',
        'Similar a comprensión de listas pero crea un set',
        'Los sets eliminan duplicados automáticamente',
        'Útil para valores únicos'
      ],
      realWorldExample: 'En un sistema de categorías, obtienes categorías únicas: {producto.categoria for producto in productos}.'
    },
    theory: `# Comprensión de Sets

## ¿Qué es Comprensión de Sets?

Las **comprensiones de sets** permiten crear sets de forma concisa.

## Sintaxis

\`\`\`python
numeros = [1, 2, 2, 3, 3, 3]
unicos = {n**2 for n in numeros}
# {1, 4, 9}
\`\`\`

## Eliminación Automática de Duplicados

Los sets eliminan duplicados automáticamente:
\`\`\`python
numeros = [1, 2, 2, 3, 3, 3]
unicos = {n for n in numeros}  # {1, 2, 3}
\`\`\`

## Comparación

### Sin Comprensión
\`\`\`python
unicos = set()
for n in numeros:
    unicos.add(n**2)
\`\`\`

### Con Comprensión
\`\`\`python
unicos = {n**2 for n in numeros}
\`\`\`

## Ventajas

- **Eliminación de duplicados**: Automática
- **Concisión**: Menos código
- **Eficiencia**: Generalmente más rápido`,
    tips: [
      'Sintaxis: {expresion for elemento in iterable}',
      'Similar a comprensión de listas pero crea un set',
      'Los sets eliminan duplicados automáticamente',
      'Útil para valores únicos',
      'Puedes agregar condiciones con if'
    ],
    advice: [
      'Usa comprensión de sets cuando necesites valores únicos',
      'Practica transformando bucles for a comprensiones',
      'Aprende cuándo usar sets vs listas',
      'Practica eliminando duplicados de diferentes formas'
    ]
  },
  {
    id: 8,
    title: 'Algoritmo de búsqueda lineal',
    description: 'Implementa búsqueda lineal para encontrar un elemento en una lista.',
    initialCode: `# Implementa búsqueda lineal
def busqueda_lineal(lista, objetivo):
    # Itera sobre la lista
    # Si encuentras el objetivo, devuelve su índice
    # Si no lo encuentras, devuelve -1
    pass

# Prueba la función
numeros = [5, 2, 8, 1, 9, 3]
resultado = busqueda_lineal(numeros, 8)
print(f"Índice de 8: {resultado}")`,
    solution: `# Implementa búsqueda lineal
def busqueda_lineal(lista, objetivo):
    # Itera sobre la lista
    # Si encuentras el objetivo, devuelve su índice
    # Si no lo encuentras, devuelve -1
    for i, elemento in enumerate(lista):
        if elemento == objetivo:
            return i
    return -1

# Prueba la función
numeros = [5, 2, 8, 1, 9, 3]
resultado = busqueda_lineal(numeros, 8)
print(f"Índice de 8: {resultado}")`,
    testCases: [],
    hints: [
      'Itera sobre la lista con enumerate()',
      'Compara cada elemento con el objetivo',
      'Si encuentras el objetivo, devuelve su índice',
      'Si terminas el bucle sin encontrarlo, devuelve -1'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué búsqueda lineal?',
      content: `La **búsqueda lineal** es un algoritmo fundamental para encontrar elementos en listas. Aunque no es la más eficiente, es simple y funciona con listas no ordenadas.

### ¿Por qué este ejercicio?
- **Fundamentos**: Algoritmo básico de búsqueda
- **Simplicidad**: Fácil de entender e implementar
- **Aplicación práctica**: Útil cuando no puedes ordenar la lista
- **Base**: Fundamentos para algoritmos más complejos

### En el mundo real:
Cuando un sistema busca un usuario en una lista pequeña, usa búsqueda lineal. Sin algoritmos de búsqueda, tendrías que revisar manualmente cada elemento.`,
      keyPoints: [
        'Búsqueda lineal revisa elementos uno por uno',
        'Funciona con listas ordenadas y no ordenadas',
        'Complejidad O(n) - revisa todos los elementos en el peor caso',
        'Simple pero no la más eficiente para listas grandes'
      ],
      realWorldExample: 'En un sistema de inventario, buscas un producto por nombre: busqueda_lineal(productos, "Laptop").'
    },
    theory: `# Búsqueda Lineal

## ¿Qué es Búsqueda Lineal?

La **búsqueda lineal** revisa elementos uno por uno hasta encontrar el objetivo.

## Algoritmo

1. **Itera** sobre la lista
2. **Compara** cada elemento con el objetivo
3. **Si coincide**: Devuelve el índice
4. **Si termina**: Devuelve -1

## Implementación

\`\`\`python
def busqueda_lineal(lista, objetivo):
    for i, elemento in enumerate(lista):
        if elemento == objetivo:
            return i
    return -1
\`\`\`

## Complejidad

- **Mejor caso**: O(1) - elemento al inicio
- **Peor caso**: O(n) - elemento al final o no existe
- **Promedio**: O(n/2)

## Ventajas y Desventajas

### Ventajas
- Simple de implementar
- Funciona con listas no ordenadas

### Desventajas
- Lenta para listas grandes
- Revisa todos los elementos en el peor caso`,
    tips: [
      'Itera sobre la lista con enumerate()',
      'Compara cada elemento con el objetivo',
      'Devuelve el índice cuando encuentres el objetivo',
      'Devuelve -1 si no lo encuentras',
      'Funciona con listas ordenadas y no ordenadas'
    ],
    advice: [
      'Practica implementando búsqueda lineal',
      'Experimenta con diferentes tipos de datos',
      'Aprende sobre complejidad algorítmica',
      'Compara con búsqueda binaria para listas ordenadas'
    ]
  },
  {
    id: 9,
    title: 'Algoritmo de ordenamiento por selección',
    description: 'Implementa ordenamiento por selección para ordenar una lista de números.',
    initialCode: `# Implementa ordenamiento por selección
def ordenamiento_seleccion(lista):
    # Crea una copia de la lista para no modificar la original
    lista_ordenada = lista.copy()
    
    # Para cada posición i en la lista
    # Encuentra el elemento más pequeño desde i hasta el final
    # Intercambia ese elemento con el elemento en posición i
    pass
    
    return lista_ordenada

# Prueba la función
numeros = [5, 2, 8, 1, 9, 3]
ordenados = ordenamiento_seleccion(numeros)
print(f"Ordenados: {ordenados}")`,
    solution: `# Implementa ordenamiento por selección
def ordenamiento_seleccion(lista):
    # Crea una copia de la lista para no modificar la original
    lista_ordenada = lista.copy()
    
    # Para cada posición i en la lista
    for i in range(len(lista_ordenada)):
        # Encuentra el índice del elemento más pequeño desde i hasta el final
        indice_minimo = i
        for j in range(i + 1, len(lista_ordenada)):
            if lista_ordenada[j] < lista_ordenada[indice_minimo]:
                indice_minimo = j
        
        # Intercambia el elemento más pequeño con el elemento en posición i
        lista_ordenada[i], lista_ordenada[indice_minimo] = lista_ordenada[indice_minimo], lista_ordenada[i]
    
    return lista_ordenada

# Prueba la función
numeros = [5, 2, 8, 1, 9, 3]
ordenados = ordenamiento_seleccion(numeros)
print(f"Ordenados: {ordenados}")`,
    testCases: [],
    hints: [
      'Para cada posición, encuentra el elemento más pequeño restante',
      'Usa un bucle anidado para buscar el mínimo',
      'Intercambia elementos usando: a, b = b, a',
      'El algoritmo ordena de menor a mayor'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué ordenamiento por selección?',
      content: `El **ordenamiento por selección** es un algoritmo fundamental de ordenamiento. Aunque no es el más eficiente, es fácil de entender y implementar.

### ¿Por qué este ejercicio?
- **Fundamentos**: Algoritmo básico de ordenamiento
- **Simplicidad**: Fácil de entender e implementar
- **Aplicación práctica**: Útil para entender cómo funcionan los ordenamientos
- **Base**: Fundamentos para algoritmos más complejos

### En el mundo real:
Aunque Python tiene sorted() que es más eficiente, entender ordenamiento por selección te ayuda a comprender cómo funcionan los algoritmos de ordenamiento. Sin algoritmos de ordenamiento, tendrías que ordenar manualmente.`,
      keyPoints: [
        'Ordenamiento por selección encuentra el mínimo en cada iteración',
        'Intercambia el mínimo con el elemento en la posición actual',
        'Complejidad O(n²) - lento para listas grandes',
        'Simple pero no el más eficiente'
      ],
      realWorldExample: 'Aunque Python tiene sorted(), entender este algoritmo te ayuda a comprender la complejidad algorítmica.'
    },
    theory: `# Ordenamiento por Selección

## ¿Qué es Ordenamiento por Selección?

El **ordenamiento por selección** encuentra el elemento más pequeño en cada iteración y lo coloca en su posición correcta.

## Algoritmo

1. **Para cada posición** i en la lista
2. **Encuentra** el elemento más pequeño desde i hasta el final
3. **Intercambia** ese elemento con el elemento en posición i

## Implementación

\`\`\`python
def ordenamiento_seleccion(lista):
    lista_ordenada = lista.copy()
    for i in range(len(lista_ordenada)):
        indice_minimo = i
        for j in range(i + 1, len(lista_ordenada)):
            if lista_ordenada[j] < lista_ordenada[indice_minimo]:
                indice_minimo = j
        lista_ordenada[i], lista_ordenada[indice_minimo] = lista_ordenada[indice_minimo], lista_ordenada[i]
    return lista_ordenada
\`\`\`

## Complejidad

- **Mejor caso**: O(n²)
- **Peor caso**: O(n²)
- **Promedio**: O(n²)

## Ventajas y Desventajas

### Ventajas
- Simple de implementar
- Fácil de entender

### Desventajas
- Lento para listas grandes
- Siempre O(n²) sin importar el orden inicial`,
    tips: [
      'Para cada posición, encuentra el elemento más pequeño restante',
      'Usa un bucle anidado para buscar el mínimo',
      'Intercambia elementos usando: a, b = b, a',
      'El algoritmo ordena de menor a mayor',
      'Crea una copia para no modificar la original'
    ],
    advice: [
      'Practica implementando ordenamiento por selección',
      'Experimenta con diferentes listas',
      'Aprende sobre complejidad algorítmica O(n²)',
      'Compara con sorted() de Python que es más eficiente'
    ]
  },
  {
    id: 10,
    title: 'Objetos y referencias en listas',
    description: 'Aprende cómo Python maneja referencias a objetos en listas y cómo afecta la modificación de listas.',
    initialCode: `# Crea una lista
lista1 = [1, 2, 3]

# Asigna lista1 a lista2
lista2 = lista1

# Modifica lista2 agregando un elemento
lista2.append(4)

# ¿Qué contiene lista1?
print(f"lista1: {lista1}")
print(f"lista2: {lista2}")

# Crea una copia real de lista1
lista3 = 

print(f"lista3: {lista3}")`,
    solution: `# Crea una lista
lista1 = [1, 2, 3]

# Asigna lista1 a lista2
lista2 = lista1

# Modifica lista2 agregando un elemento
lista2.append(4)

# ¿Qué contiene lista1?
print(f"lista1: {lista1}")
print(f"lista2: {lista2}")

# Crea una copia real de lista1
lista3 = lista1.copy()

print(f"lista3: {lista3}")`,
    testCases: [],
    hints: [
      'En Python, las listas son objetos y se pasan por referencia',
      'lista2 = lista1 crea una referencia, no una copia',
      'Modificar lista2 también modifica lista1',
      'Usa .copy() para crear una copia real'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué entender referencias?',
      content: `Entender cómo Python maneja **referencias a objetos** es crucial para evitar bugs. Las listas se pasan por referencia, no por valor.

### ¿Por qué este ejercicio?
- **Evitar bugs**: Entender referencias previene errores comunes
- **Comportamiento de Python**: Cómo Python maneja objetos mutables
- **Aplicación práctica**: Muy importante al trabajar con listas y diccionarios
- **Fundamentos**: Base para entender programación orientada a objetos

### En el mundo real:
Cuando un sistema modifica una lista que se pasa como parámetro, la lista original también se modifica. Sin entender referencias, podrías tener bugs difíciles de encontrar.`,
      keyPoints: [
        'Las listas se pasan por referencia, no por valor',
        'lista2 = lista1 crea una referencia, no una copia',
        'Modificar una referencia modifica el objeto original',
        'Usa .copy() para crear una copia real'
      ],
      realWorldExample: 'En un sistema de procesamiento, modificar una lista de productos que se pasa como parámetro también modifica la lista original.'
    },
    theory: `# Objetos y Referencias en Listas

## ¿Cómo Funcionan las Referencias?

En Python, las listas son **objetos** y se pasan por **referencia**, no por valor.

## Asignación de Referencias

\`\`\`python
lista1 = [1, 2, 3]
lista2 = lista1  # lista2 apunta a la misma lista que lista1
lista2.append(4)
print(lista1)  # [1, 2, 3, 4] - lista1 también se modifica
\`\`\`

## Crear Copias Reales

Para crear una copia real, usa \`.copy()\`:
\`\`\`python
lista1 = [1, 2, 3]
lista3 = lista1.copy()  # Copia real
lista3.append(4)
print(lista1)  # [1, 2, 3] - lista1 no se modifica
\`\`\`

## Otros Métodos de Copia

### Slicing
\`\`\`python
lista3 = lista1[:]  # También crea una copia
\`\`\`

### list()
\`\`\`python
lista3 = list(lista1)  # También crea una copia
\`\`\`

## Ventajas y Desventajas

### Ventajas de Referencias
- Eficiente en memoria
- Modificaciones se reflejan automáticamente

### Desventajas
- Puede causar bugs si no entiendes el comportamiento
- Modificaciones inesperadas`,
    tips: [
      'Las listas se pasan por referencia, no por valor',
      'lista2 = lista1 crea una referencia, no una copia',
      'Modificar una referencia modifica el objeto original',
      'Usa .copy() para crear una copia real',
      'También puedes usar slicing: lista[:]'
    ],
    advice: [
      'Practica con referencias y copias',
      'Experimenta modificando listas',
      'Aprende cuándo usar referencias vs copias',
      'Practica identificando bugs relacionados con referencias'
    ]
  }
];
